import { Risco } from '../types';
import { atualizarHRNRisco } from '../utils/hrn-calculator';
import { TABELA_PO, TABELA_FE, TABELA_GS, TABELA_NP, formatarValorHRN } from '../../constants/hrn-tables';
import { useI18n } from '@/contexts/i18n-hook';

/** Faixas da tabela do cliente (Arcelor Mittal). A chave define a cor e o rótulo. */
type ChaveClassificacao =
  | 'inaceitavel'
  | 'extremo'
  | 'muitoAlto'
  | 'alto'
  | 'significante'
  | 'baixo'
  | 'muitoBaixo'
  | 'aceitavel';

const CLASSIFICACAO_CRITICA: ChaveClassificacao[] = ['inaceitavel', 'extremo', 'muitoAlto'];
const CLASSIFICACAO_ATENCAO: ChaveClassificacao[] = ['alto', 'significante', 'baixo'];

function corClassificacao(chave: ChaveClassificacao): string {
  if (CLASSIFICACAO_CRITICA.includes(chave)) return 'bg-red-100 text-red-800';
  if (CLASSIFICACAO_ATENCAO.includes(chave)) return 'bg-yellow-100 text-gray-800';
  return 'bg-green-100 text-green-800';
}

interface HrnTablesSectionProps {
  riscosEditados: Risco[];
  setRiscosEditados: (riscos: Risco[]) => void;
  exibirHRN: boolean;
  setExibirHRN: (exibir: boolean) => void;
}

export function HrnTablesSection({ riscosEditados, setRiscosEditados, exibirHRN, setExibirHRN }: HrnTablesSectionProps) {
  const { t } = useI18n();

  // Classificação conforme tabela Cliente (Arcelor Mittal)
  const getClassificacao = (hrn: number): ChaveClassificacao => {
    if (hrn >= 1000) return 'inaceitavel';
    if (hrn >= 500) return 'extremo';
    if (hrn >= 100) return 'muitoAlto';
    if (hrn >= 50) return 'alto';
    if (hrn >= 10) return 'significante';
    if (hrn >= 5) return 'baixo';
    if (hrn >= 1) return 'muitoBaixo';
    return 'aceitavel';
  };

  const handleAtualizarHRN = (index: number, campo: string, valor: string | number, tipo: 'atual' | 'futuro') => {
    atualizarHRNRisco(riscosEditados, index, campo, valor, tipo, setRiscosEditados);
  };

  return (
    <div className="space-y-6">
      {/* Checkbox para exibir/ocultar HRN no documento */}
      <div className="flex items-center space-x-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <input
          type="checkbox"
          id="exibirHRN"
          checked={exibirHRN}
          onChange={(e) => setExibirHRN(e.target.checked)}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="exibirHRN" className="text-sm font-medium text-gray-700">
          {t('apreciacaoModais.editarParte.hrn.exibirRotulo')}
        </label>
        {!exibirHRN && (
          <span className="text-xs text-orange-600 ml-2">
            {t('apreciacaoModais.editarParte.hrn.exibirAviso')}
          </span>
        )}
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('apreciacaoModais.editarParte.hrn.tituloAtual')}</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 border border-gray-300 text-left">{t('apreciacaoModais.editarParte.hrn.colRisco')}</th>
                <th className="px-4 py-2 border border-gray-300 text-center">PO</th>
                <th className="px-4 py-2 border border-gray-300 text-center">FE</th>
                <th className="px-4 py-2 border border-gray-300 text-center">GS</th>
                <th className="px-4 py-2 border border-gray-300 text-center">NP</th>
                <th className="px-4 py-2 border border-gray-300 text-center">HRN</th>
                <th className="px-4 py-2 border border-gray-300 text-center">{t('apreciacaoModais.editarParte.hrn.colClassificacao')}</th>
              </tr>
            </thead>
            <tbody>
              {riscosEditados.map((risco, index) => {
                const nomeRisco = risco.eventoPerigoso || risco.consequencias || risco.parte || t('apreciacaoModais.editarParte.hrn.riscoFallback', { numero: risco.numero || index + 1 });
                const classificacao = getClassificacao(risco.hrnAtual || 0);
                return (
                  <tr key={index}>
                    <td className="px-4 py-2 border border-gray-300 text-sm">{nomeRisco}</td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      <select
                        value={risco.probabilidadeAtual ?? 1}
                        onChange={(e) => handleAtualizarHRN(index, 'probabilidade', Number(e.target.value), 'atual')}
                        className="w-full text-center border border-gray-300 rounded text-sm px-2 py-1"
                      >
                        {TABELA_PO.map((item) => (
                          <option key={item.valor} value={item.valor}>
                            {formatarValorHRN('PO', item.valor)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      <select
                        value={risco.frequenciaAtual ?? 1}
                        onChange={(e) => handleAtualizarHRN(index, 'frequencia', Number(e.target.value), 'atual')}
                        className="w-full text-center border border-gray-300 rounded text-sm px-2 py-1"
                      >
                        {TABELA_FE.map((item) => (
                          <option key={item.valor} value={item.valor}>
                            {formatarValorHRN('FE', item.valor)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      <select
                        value={risco.gravidadeAtual ?? 1}
                        onChange={(e) => handleAtualizarHRN(index, 'gravidade', Number(e.target.value), 'atual')}
                        className="w-full text-center border border-gray-300 rounded text-sm px-2 py-1"
                      >
                        {TABELA_GS.map((item) => (
                          <option key={item.valor} value={item.valor}>
                            {formatarValorHRN('GS', item.valor)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      <select
                        value={risco.exposicaoAtual ?? 1}
                        onChange={(e) => handleAtualizarHRN(index, 'exposicao', Number(e.target.value), 'atual')}
                        className="w-full text-center border border-gray-300 rounded text-sm px-2 py-1"
                      >
                        {TABELA_NP.map((item) => (
                          <option key={item.valor} value={item.valor}>
                            {formatarValorHRN('NP', item.valor)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center font-semibold bg-gray-50">
                      {risco.hrnAtual ?? 0}
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${corClassificacao(classificacao)}`}>
                        {t(`apreciacaoModais.editarParte.hrn.classificacao.${classificacao}`)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('apreciacaoModais.editarParte.hrn.tituloFuturo')}</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 border border-gray-300 text-left">{t('apreciacaoModais.editarParte.hrn.colRisco')}</th>
                <th className="px-4 py-2 border border-gray-300 text-center">PO</th>
                <th className="px-4 py-2 border border-gray-300 text-center">FE</th>
                <th className="px-4 py-2 border border-gray-300 text-center">GS</th>
                <th className="px-4 py-2 border border-gray-300 text-center">NP</th>
                <th className="px-4 py-2 border border-gray-300 text-center">HRN</th>
                <th className="px-4 py-2 border border-gray-300 text-center">{t('apreciacaoModais.editarParte.hrn.colClassificacao')}</th>
              </tr>
            </thead>
            <tbody>
              {riscosEditados.map((risco, index) => {
                const nomeRisco = risco.eventoPerigoso || risco.consequencias || risco.parte || t('apreciacaoModais.editarParte.hrn.riscoFallback', { numero: risco.numero || index + 1 });
                const hrnFuturo = risco.hrnFuturo || risco.hrnAtual || 0;
                const classificacao = getClassificacao(hrnFuturo);
                return (
                  <tr key={index}>
                    <td className="px-4 py-2 border border-gray-300 text-sm">{nomeRisco}</td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      <select
                        value={risco.probabilidadeFuturo ?? risco.probabilidadeAtual ?? 1}
                        onChange={(e) => handleAtualizarHRN(index, 'probabilidade', Number(e.target.value), 'futuro')}
                        className="w-full text-center border border-gray-300 rounded text-sm px-2 py-1"
                      >
                        {TABELA_PO.map((item) => (
                          <option key={item.valor} value={item.valor}>
                            {formatarValorHRN('PO', item.valor)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      <select
                        value={risco.frequenciaFuturo ?? risco.frequenciaAtual ?? 1}
                        onChange={(e) => handleAtualizarHRN(index, 'frequencia', Number(e.target.value), 'futuro')}
                        className="w-full text-center border border-gray-300 rounded text-sm px-2 py-1"
                      >
                        {TABELA_FE.map((item) => (
                          <option key={item.valor} value={item.valor}>
                            {formatarValorHRN('FE', item.valor)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      <select
                        value={risco.gravidadeFuturo ?? risco.gravidadeAtual ?? 1}
                        onChange={(e) => handleAtualizarHRN(index, 'gravidade', Number(e.target.value), 'futuro')}
                        className="w-full text-center border border-gray-300 rounded text-sm px-2 py-1"
                      >
                        {TABELA_GS.map((item) => (
                          <option key={item.valor} value={item.valor}>
                            {formatarValorHRN('GS', item.valor)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      <select
                        value={risco.exposicaoFuturo ?? risco.exposicaoAtual ?? 1}
                        onChange={(e) => handleAtualizarHRN(index, 'exposicao', Number(e.target.value), 'futuro')}
                        className="w-full text-center border border-gray-300 rounded text-sm px-2 py-1"
                      >
                        {TABELA_NP.map((item) => (
                          <option key={item.valor} value={item.valor}>
                            {formatarValorHRN('NP', item.valor)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center font-semibold bg-gray-50">
                      {hrnFuturo}
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${corClassificacao(classificacao)}`}>
                        {t(`apreciacaoModais.editarParte.hrn.classificacao.${classificacao}`)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

